/**
 * Время всегда показываем в поясе ЦЕНТРА (`me.center.tz`), а не устройства.
 * Родитель в отпуске в другом поясе не должен увидеть, что занятие «в 6 утра».
 *
 * Отсюда правило: `new Date(...).toLocaleString()` без `timeZone` в этом проекте
 * запрещён — форматируйте только функциями из этого файла.
 *
 * Единицы: метки времени — epoch ms UTC (число), рабочие часы — минуты от
 * полуночи в поясе центра, день недели — 0 = понедельник (как на сервере).
 */

export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;

const formatters = new Map<string, Intl.DateTimeFormat>();

function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('ru-RU', { timeZone: tz, ...opts });
    formatters.set(key, f);
  }
  return f;
}

/** Смещение пояса в минутах для конкретного момента (учитывает переход на летнее время). */
export function tzOffsetMinutes(tz: string, at: number): number {
  const parts: Record<string, string> = {};
  for (const p of fmt(tz, {
    hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(at))) parts[p.type] = p.value;
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
  return (asUTC - Math.floor(at / 1000) * 1000) / MINUTE;
}

export type LocalParts = { date: string; minutes: number; weekday: number };

/** epoch ms → локальные дата, минуты от полуночи и день недели (0 = понедельник). */
export function toLocal(ts: number, tz: string): LocalParts {
  const shifted = new Date(ts + tzOffsetMinutes(tz, ts) * MINUTE);
  return {
    date: shifted.toISOString().slice(0, 10),
    minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
    weekday: (shifted.getUTCDay() + 6) % 7,
  };
}

/** 'YYYY-MM-DD' + минуты локального времени → epoch ms. */
export function localToUtc(dateISO: string, minutes: number, tz: string): number {
  const [y, m, d] = dateISO.split('-').map(Number);
  const naive = Date.UTC(y, m - 1, d, 0, minutes);
  // второй проход: около перевода часов смещение первой догадки может отличаться
  const once = naive - tzOffsetMinutes(tz, naive) * MINUTE;
  return naive - tzOffsetMinutes(tz, once) * MINUTE;
}

export const isoDate = (ts: number, tz: string): string => toLocal(ts, tz).date;
export const todayISO = (tz: string, now = Date.now()): string => isoDate(now, tz);

export function addDaysISO(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** 0 = понедельник. */
export function weekdayOfISO(dateISO: string): number {
  const [y, m, d] = dateISO.split('-').map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

// --- рабочие часы: минуты от полуночи -------------------------------------

export function minutesToHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function parseHHMM(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

const WEEKDAYS_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const WEEKDAYS_LONG = ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'];
const WEEKDAYS_ON = ['в понедельник', 'во вторник', 'в среду', 'в четверг', 'в пятницу', 'в субботу', 'в воскресенье'];

/** 0 = понедельник. `form`: 'short' — «пн», 'long' — «понедельник», 'on' — «во вторник». */
export function weekdayRu(weekday: number, form: 'short' | 'long' | 'on' = 'short'): string {
  const i = ((weekday % 7) + 7) % 7;
  if (form === 'long') return WEEKDAYS_LONG[i];
  if (form === 'on') return WEEKDAYS_ON[i];
  return WEEKDAYS_SHORT[i];
}

// --- форматирование в поясе центра ----------------------------------------

/** Формат показа времени, настройка организации (`me.center.timeFormat`). */
export type TimeFormat = '12' | '24';

/**
 * Минуты от полуночи → время для показа. Считаем руками, а не через Intl: ровно
 * этот же алгоритм лежит в `src/core/time.ts` (humanTime), а бот и приложение
 * обязаны показывать одну и ту же запись одинаково — до символа.
 *
 * Это ФОРМАТИРОВАНИЕ. Для значений полей ввода и ключей API есть minutesToHHMM,
 * он всегда 24-часовой и от настройки не зависит.
 */
export function formatMinutes(minutes: number, format: TimeFormat): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (format === '12') {
    const suffix = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** «14:30» или «2:30 PM» */
export const formatTime = (ts: number, tz: string, format: TimeFormat): string =>
  formatMinutes(toLocal(ts, tz).minutes, format);

/** «12 мая» */
export const formatDay = (ts: number, tz: string): string =>
  fmt(tz, { day: 'numeric', month: 'long' }).format(ts);

/** «12 мая 2026» */
export const formatDayYear = (ts: number, tz: string): string =>
  fmt(tz, { day: 'numeric', month: 'long', year: 'numeric' }).format(ts);

/** «вт, 12 мая» */
export const formatDayWithWeekday = (ts: number, tz: string): string =>
  `${weekdayRu(toLocal(ts, tz).weekday)}, ${formatDay(ts, tz)}`;

/** «12 мая, 14:30» */
export const formatDateTime = (ts: number, tz: string, format: TimeFormat): string =>
  `${formatDay(ts, tz)}, ${formatTime(ts, tz, format)}`;

/** «14:30 – 15:20» — интервал приёма. */
export const formatRange = (from: number, to: number, tz: string, format: TimeFormat): string =>
  `${formatTime(from, tz, format)} – ${formatTime(to, tz, format)}`;

/** 'YYYY-MM-DD' → «вт, 12 мая» без обратного пересчёта в UTC. */
export function formatISODay(dateISO: string, opts: { weekday?: boolean } = {}): string {
  const [y, m, d] = dateISO.split('-').map(Number);
  const asUTC = Date.UTC(y, m - 1, d, 12);
  const day = new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', day: 'numeric', month: 'long' }).format(asUTC);
  return opts.weekday === false ? day : `${weekdayRu(weekdayOfISO(dateISO))}, ${day}`;
}

/** «сегодня» / «завтра» / «вт, 12 мая» — для заголовков дней в сетке слотов. */
export function humanISODay(dateISO: string, tz: string, now = Date.now()): string {
  const today = todayISO(tz, now);
  if (dateISO === today) return 'сегодня';
  if (dateISO === addDaysISO(today, 1)) return 'завтра';
  return formatISODay(dateISO);
}

/**
 * Обратный отсчёт для таймера брони: «14:59», «0:07».
 * Отрицательный остаток превращается в «0:00» — истёкшую бронь показывать минусом незачем.
 */
export function countdown(msLeft: number): string {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
